/**
 * Debug page for selections viewer
 * @module
 */
import { createFileRoute } from "@tanstack/react-router";
import type {
  CompositorCategoryKeyToSelectionKeyMapping,
  CompositorManifest,
} from "api-types/selections/selectionsCompositorTypes";
import { assertIsNotUndefined, assertIsString, ensureDefined } from "common/utils/assertOfType";
import { formatYYYYMMDD } from "common/utils/dates";
import Toggle from "lib/components/actions/Toggle";
import styles from "lib/components/debug/selections.module.scss";
import SelectionItems from "lib/components/selections/SelectionItems";
import selectionItemStyles from "lib/components/selections/SelectionItems.module.scss";
import SelectionsViewer from "lib/components/selections/SelectionsViewer";
import useSelectionsViewer from "lib/components/selections/useSelectionsViewer";
import type { SelectionsViewerContextProps } from "lib/state/SelectionsViewerContext";
import SelectionsViewerContext from "lib/state/SelectionsViewerContext";
import {
  checkUnrealRender,
  compositeSelections,
  getConfiguration,
  getSceneVersionIds,
  getSceneVersionManifestJson,
  startUnrealRender,
} from "lib/storage/selectionsStorage";
import getPageTitle from "lib/utils/getPageTitle";
import type { ReactNode } from "react";
import { useEffect, useRef, useState } from "react";
import { Helmet as Head } from "react-helmet-async";
import { COMPOSITOR_DEFAULT_RESULTS_CACHE_ID } from "selections-compositor/state/compositorDefaults";
import { parseCompositorManifest, validateCompositorManifest } from "selections-compositor/state/selections";
import type { SelectionsPipelineRenderRequest } from "selections-compositor/state/selectionsPipeline";
import Loading from "ui/components/Loading";
import Modal from "ui/components/Modal";
import TabbedMenu from "ui/components/TabbedMenu";
import type { OptionProps } from "ui/forms/Select";
import Select from "ui/forms/Select";
import useAppContext from "ui/hooks/useAppContext";
import IconGear from "ui/icons/IconGear";
import withAuthentication from "../../components/withAuthentication";

const startingManifest: CompositorManifest = {
  builderId: "Higharc",
  projectId: "Orion-IBS-2023",
  sceneId: "epcon-bathroom",
  sceneVersionId: "20220909-test",
  resultsCacheId: COMPOSITOR_DEFAULT_RESULTS_CACHE_ID,
  builderLogoUrl: "https://photoreal-test.s3.amazonaws.com/logos/Epcon_logo.jpeg",

  plasticRepository: "Photoreal_Bathroom",
  unrealScenePath: "/Game/Main",
  unrealCamera: "Camera360",

  baselineImageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/baseline.png",
  compositingGraph: {},
  globalLayers: {},
  compositingOrder: [
    ["cabinets", "base"],
    ["floors", "base"],
  ],
  categoryOrder: ["cabinets", "floors"],
  categories: {
    cabinets: {
      hashCode: "0",
      prettyName: "Cabinets",
      baselineSelectionKey: "cabinet_gray",
      defaultSelectionKey: "cabinet_gray",
      selections: {
        cabinet_blue: {
          hashCode: "0",
          prettyName: "Metallic Blue",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_blue_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_blue.png",
            },
          },
        },
        cabinet_brown: {
          hashCode: "1",
          prettyName: "Kraftmaid Driftwood",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_brown_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_brown.png",
            },
          },
        },
        cabinet_gray: {
          hashCode: "2",
          prettyName: "Kraftmaid Pebblegray",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_gray_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/cabinet/cabinet_gray.png",
            },
          },
        },
      },
    },
    floors: {
      hashCode: "1",
      prettyName: "Floors",
      baselineSelectionKey: "floor_white",
      defaultSelectionKey: "floor_white",
      selections: {
        floor_black: {
          hashCode: "0",
          prettyName: "Black Marble",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_black_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_black.png",
            },
          },
        },
        floor_blue: {
          hashCode: "1",
          prettyName: "Blue",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_blue_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_blue.png",
            },
          },
        },
        floor_green: {
          hashCode: "2",
          prettyName: "Space Green",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_green_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_green.png",
            },
          },
        },
        floor_white: {
          hashCode: "3",
          prettyName: "White Marble",
          thumbnailUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_white_tb.png",
          layers: {
            base: {
              imageUrl: "https://photoreal-test.s3.amazonaws.com/selections/20220909-test/floor/floor_white.png",
            },
          },
        },
      },
    },
  },
};

function SelectionsDebugPage() {
  const { setUserNotification } = useAppContext();

  const {
    manifest,
    setManifest,
    activeSelections,
    scene,
    setScene,
    menuItems,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
  } = useSelectionsViewer({
    onActiveSelections: async (newActiveSelections: CompositorCategoryKeyToSelectionKeyMapping) => {
      await updateSelections(newActiveSelections);
    },
  });

  const [sceneOptions, setSceneOptions] = useState<Array<OptionProps>>([]);

  const [debugModalOpen, setDebugOpenModal] = useState<boolean>(false);
  const [debugModalStatus, setDebugModalStatus] = useState<ReactNode>();
  const [inPresentationMode, setInPresentationMode] = useState<boolean>(false);
  const [tileCompositorEnabled, setTileCompositorEnabled] = useState<boolean>(false);

  const unrealRenderIntervalId = useRef<NodeJS.Timeout | undefined>(undefined);
  const [unrealRenderStartTime, setUnrealRenderStartTime] = useState<Date>();
  const [unrealRenderProgress, setUnrealRenderProgress] = useState<string>();

  const [tilesRefreshLastTimestamp, setTilesRefreshLastTimestamp] = useState<number>();

  const selectionsViewerContext: SelectionsViewerContextProps = {
    manifest,
    currentCategoryKey,
    currentSelectionKey,
    activateSelection,
    menuItems,
    activeSelections,
  };

  useEffect(() => {
    document.body.style.overflow = "hidden"; // Important to prevent scrolling on iPad

    try {
      validateCompositorManifest(startingManifest);
      setManifest(startingManifest);

      async function setUpSceneVersions() {
        const sceneVersionIds = await getSceneVersionIds();

        const newSceneOptions: OptionProps[] = sceneVersionIds.map((sceneVersionId) => {
          return { value: sceneVersionId, label: sceneVersionId };
        });
        setSceneOptions(newSceneOptions);
      }
      void setUpSceneVersions();
    } catch (e) {
      setUserNotification({
        message: "Could not validate starting manifest:\n" + JSON.stringify(e),
        messageType: "error",
      });
    }
  }, []);

  useEffect(() => {
    // Disable tile compositor outside of presentation mode
    if (!inPresentationMode && tileCompositorEnabled) {
      setTileCompositorEnabled(false);
    }
  }, [inPresentationMode]);

  useEffect(() => {
    if (!unrealRenderStartTime) {
      setUnrealRenderProgress("");
      return undefined;
    }

    const intervalId = setInterval(() => {
      if (!unrealRenderStartTime) {
        clearInterval(intervalId);
      }

      const elapsedMs = Date.now() - unrealRenderStartTime.valueOf();
      const elapsedS = Math.floor(elapsedMs / 1000);

      setUnrealRenderProgress(`Rendering since ${unrealRenderStartTime.toLocaleTimeString()} (duration: ${elapsedS}s)`);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [unrealRenderStartTime]);

  async function updateSelections(newActiveSelections: CompositorCategoryKeyToSelectionKeyMapping) {
    if (tileCompositorEnabled) {
      setTilesRefreshLastTimestamp(Date.now());
    } else {
      const result = await compositeSelections({
        manifest,
        activeSelections: newActiveSelections,
      });
      if (result) {
        setScene({ textureUrl: result.imageUrl });
      }
    }
  }

  function loadSceneFromJson(manifestJson: string) {
    try {
      const manifest = parseCompositorManifest(manifestJson);
      setManifest(manifest);
    } catch (e) {
      setUserNotification({
        message: "Could not parse manifest:\n" + JSON.stringify(e),
        messageType: "error",
      });
    }
  }

  async function loadSceneFromId(sceneId: string) {
    const manifestJson = await getSceneVersionManifestJson(sceneId);
    if (manifestJson) {
      loadSceneFromJson(manifestJson);
    }
  }

  function loadSceneFromFile() {
    const fileDialog = document.createElement("input");
    fileDialog.type = "file";

    fileDialog.onchange = (e: Event) => {
      const target = e.target as HTMLInputElement;

      if (!target.files || target.files.length === 0) {
        return;
      }

      const reader = new FileReader();
      reader.onload = async (e) => {
        if (e.target) {
          const manifestJson = e.target.result as string;
          loadSceneFromJson(manifestJson);
        }
      };

      reader.readAsText(target.files[0]);
    };

    fileDialog.click();
  }

  async function triggerUnrealRender() {
    try {
      const outputPath = `selections/unreal-render-test/${formatYYYYMMDD()}-cabinets_0_${Date.now().toString()}.png`;
      const outputUrl = `https://photoreal-test.s3.amazonaws.com/${outputPath}`;

      const intervalId = setInterval(async () => {
        const isRenderReady = await checkUnrealRender(outputUrl);
        if (isRenderReady) {
          if (unrealRenderIntervalId.current) {
            clearInterval(unrealRenderIntervalId.current);
          }
          setUnrealRenderStartTime(undefined);
          setScene({ textureUrl: outputUrl });
        }
      }, 10000);
      unrealRenderIntervalId.current = intervalId;
      setUnrealRenderStartTime(new Date());

      assertIsNotUndefined(manifest);

      const renderRequest: SelectionsPipelineRenderRequest = {
        project_id: ensureDefined(manifest.projectId),
        scene_id: ensureDefined(manifest.sceneId),
        scene_version_id: ensureDefined(manifest.sceneVersionId),

        plastic_repository: manifest.plasticRepository,
        unreal_scene_path: manifest.unrealScenePath,
        upload_s3_bucket: "photoreal-test",

        selections: [
          {
            width: 1024,
            height: 512,
            num_samples: 1,
            camera_label: "Camera360",
            selection_category: "cabinets",
            selection_indices: [0],
            layers: ["base_layer"],
            upload_paths: {
              base_layer: [outputPath],
            },
          },
        ],
      };
      await startUnrealRender(renderRequest);
    } catch (e) {
      setUserNotification({
        message: "Could not trigger Unreal render:\n" + JSON.stringify(e),
        messageType: "error",
      });
    }
  }

  async function showConfiguration() {
    const config = await getConfiguration();
    setDebugModalStatus(<div>{JSON.stringify(config, null, 2)}</div>);
  }

  if (!manifest) {
    return <Loading />;
  }

  return (
    <>
      <Head>
        <title>{getPageTitle("Selections")}</title>
      </Head>
      <SelectionsViewerContext.Provider value={selectionsViewerContext}>
        <div className={styles.selectionsPage}>
          <IconGear className="icon-gear" onClick={() => setDebugOpenModal(!debugModalOpen)} />
          {!inPresentationMode && (
            <>
              <div id="menuBar" className={styles.selectionsMenuBar}>
                {manifest?.sceneId && (
                  <div className={styles.sceneSelector}>
                    <Select
                      placeholder="Available scenes"
                      selectSize="medium"
                      onChange={async (sceneId) => {
                        assertIsString(sceneId);
                        await loadSceneFromId(sceneId);
                      }}
                      options={sceneOptions}
                      value={manifest.sceneVersionId}
                    />
                  </div>
                )}
                <button onClick={loadSceneFromFile}>Load scene</button>
                <button onClick={triggerUnrealRender}>Render in Unreal</button>
                {unrealRenderStartTime && <div>{unrealRenderProgress}</div>}
              </div>
              <>
                <div id="container">
                  <img src={scene?.textureUrl} crossOrigin="anonymous" height="600" />
                </div>
                <div id="selections" className={styles.selectionsTabs}>
                  <TabbedMenu menuItems={menuItems} />
                  <SelectionItems classNames={selectionItemStyles.selectionItem} />
                </div>
              </>
            </>
          )}
          {inPresentationMode && (
            <SelectionsViewer tilesRefreshLastTimestamp={tilesRefreshLastTimestamp} scene={scene} setScene={setScene} />
          )}
          <Modal
            open={debugModalOpen}
            onClose={() => setDebugModalStatus(undefined)}
            size="small"
            title="Compositor Debug Options"
          >
            <div className={styles.debugModalOpen}>
              <button onClick={showConfiguration}>Show configuration</button>
              <div className={styles.toggle}>
                <Toggle isTrue={inPresentationMode} onChange={() => setInPresentationMode(!inPresentationMode)} />
                <label>Presentation mode</label>
              </div>
              {inPresentationMode && (
                <>
                  <div className={styles.toggle}>
                    <Toggle
                      isTrue={tileCompositorEnabled}
                      onChange={() => setTileCompositorEnabled(!tileCompositorEnabled)}
                    />
                    <label>Tile-based </label>
                  </div>
                </>
              )}
              <hr />
              {debugModalStatus && <div className="debugModalStatus">{debugModalStatus}</div>}
            </div>
          </Modal>
        </div>
      </SelectionsViewerContext.Provider>
    </>
  );
}

export const Route = createFileRoute("/debug/selections")({
  component: withAuthentication(SelectionsDebugPage, { superAdmin: true }),
});
